import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactPlayer from "react-player";
import { Helmet } from "react-helmet";
import MicrosoftClarity from "../components/MicrosoftClarity";
import WhatsAppButton from "../components/WhatsAppButton";
import LandingChatbot from "../components/LandingChatbot";
import SocialButtons from "../components/SocialButton";
import "./AudienciaLanding.css";

const AudienciaLanding = () => {
  const { usuario } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showReplayButton, setShowReplayButton] = useState(false);
  const [played, setPlayed] = useState(0);
  const playerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("Buscando campaña para usuario:", usuario);
        const response = await axios.get(
          `https://api.mrcirp.com/public/campanas-audiencia/enlace/${usuario}`
        );
        
        console.log("Respuesta del API:", response.data);
        
        if (response.data && response.data.length > 0) {
          console.log("Campaña encontrada:", response.data[0]);
          setData(response.data[0]);
        } else {
          console.log("No se encontró la campaña");
          setError("Campaña no encontrada");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        console.error("Error response:", error.response);
        setError("Error al cargar la campaña: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (usuario) {
      fetchData();
    }
  }, [usuario]);

  const handleProgress = (state) => {
    if (!playerRef.current.seeking) {
      setPlayed(state.played);
    } else {
      playerRef.current.seekTo(played);
    }
  };

  const handleEnded = () => {
    setShowReplayButton(true);
  };

  const handleReplay = () => {
    setShowReplayButton(false);
    setPlayed(0);
    if (playerRef.current) {
      playerRef.current.seekTo(0);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando campaña...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h1>404 - Campaña no encontrada</h1>
          <p>La campaña que buscas no existe o ha sido eliminada.</p>
          <a href="/" className="btn-home">Volver al inicio</a>
        </div>
      </div>
    );
  }

  // Parsear servicios_items si es un string JSON
  let serviciosItems = [];
  if (data.servicios_items) {
    try {
      serviciosItems = typeof data.servicios_items === 'string' 
        ? JSON.parse(data.servicios_items) 
        : data.servicios_items;
    } catch (e) {
      console.error('Error parsing servicios_items:', e);
    }
  }

  return (
    <div className="audiencia-landing">
      <Helmet>
        <title>{data.nombre_campana}</title>
        <meta name="description" content={data.descripcion} />
        <meta property="og:title" content={data.nombre_campana} />
        <meta property="og:description" content={data.descripcion} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Banner Principal */}
      <section 
        className="banner-section"
        style={{
          backgroundImage: data.banner_imagen_url ? `url(${data.banner_imagen_url})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container">
          <div className="banner-content">
            <h1 className="banner-title" style={{ color: 'white' }}>
              {data.banner_titulo || 'Bienvenido'}
            </h1>
            <p className="banner-subtitle" style={{ color: 'white' }}>
              {data.banner_subtitulo || 'Descubre lo que tenemos para ti'}
            </p>
            
            {/* Video del Banner */}
            {data.banner_video_url && (
              <div className="video-container">
                <ReactPlayer
                  ref={playerRef}
                  url={data.banner_video_url}
                  width="100%"
                  height="100%"
                  controls={true}
                  onProgress={handleProgress}
                  onEnded={handleEnded}
                  className="banner-video"
                />
              </div>
            )}

            {/* Imagen del Banner */}
            {data.banner_imagen_url && !data.banner_video_url && (
              <div className="image-container">
                <img 
                  src={data.banner_imagen_url} 
                  alt={data.banner_titulo}
                  className="banner-image"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sección de Servicios */}
      {data.servicios_titulo && (
        <section className="servicios-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{data.servicios_titulo}</h2>
              <p className="section-description">{data.servicios_descripcion}</p>
            </div>
            
            {serviciosItems.length > 0 && (
              <div className="servicios-grid">
                {serviciosItems.map((item, index) => (
                  <div key={index} className="servicio-card">
                    <h3 className="servicio-title">{item.titulo}</h3>
                    <p className="servicio-description">{item.descripcion}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sección de Dos Columnas (Imagen Izquierda) */}
      {data.dos_columnas_titulo && (
        <section className="dos-columnas-section">
          <div className="container">
            <div className="dos-columnas-content">
              <div className="column-left">
                {data.dos_columnas_imagen && (
                  <div className="image-container">
                    <img 
                      src={data.dos_columnas_imagen} 
                      alt={data.dos_columnas_titulo}
                      className="section-image"
                    />
                  </div>
                )}
              </div>
              <div className="column-right">
                <h2 className="section-title">{data.dos_columnas_titulo}</h2>
                <p className="section-description">{data.dos_columnas_descripcion}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sección de Dos Columnas (Imagen Derecha) */}
      {data.dos_columnas_derecha_titulo && (
        <section className="dos-columnas-section reverse">
          <div className="container">
            <div className="dos-columnas-content">
              <div className="column-right">
                <h2 className="section-title">{data.dos_columnas_derecha_titulo}</h2>
                <p className="section-description">{data.dos_columnas_derecha_descripcion}</p>
              </div>
              <div className="column-left">
                {data.dos_columnas_derecha_imagen && (
                  <div className="image-container">
                    <img 
                      src={data.dos_columnas_derecha_imagen} 
                      alt={data.dos_columnas_derecha_titulo}
                      className="section-image"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <p className="footer-text">
              © 2024 CIRP - Constructor de Ingresos Residuales Perpetuos
            </p>
          </div>
        </div>
      </footer>

      {/* Botones de Usuario */}
      <WhatsAppButton
        phoneNumber={data?.whatsapp || ""}
        message="Hola, me gustaría obtener más información sobre esta campaña."
      />
      
      <LandingChatbot />
      
      {/* Botones Sociales - Solo si hay al menos un enlace social */}
      {(data?.facebook || data?.instagram || data?.linkedin || data?.tiktok || data?.calendly || data?.pdf) && (
        <SocialButtons
          pdfUrl={data?.pdf || undefined}
          facebookUrl={data?.facebook || undefined}
          instagramUrl={data?.instagram || undefined}
          linkedinUrl={data?.linkedin || undefined}
          tiktokUrl={data?.tiktok || undefined}
          calendlyUrl={data?.calendly || undefined}
          onContactClick={(network) => console.log(`Se hizo clic en ${network}`)}
        />
      )}
    </div>
  );
};

export default AudienciaLanding;
